import React from "react";
import { Box } from "@mui/material";

import useThemeStore from "@/shared/stores/useThemeStore";
import useBookIconStore from "@/shared/stores/useBookIconStore";
import SingleImageDisplay from "./talent-experience-display/SingleImageDisplay";
import ImageGridDisplay from "./talent-experience-display/ImageGridDisplay";
import TextDisplay from "../../card-landing/experience-previews/TextDisplay";
import VideoDisplay from "../../card-landing/experience-previews/VideoDisplay";
import AudioDisplay from "./talent-experience-display/AudioDisplay";
import CardExpInteraction, {
  CardExpInteractionVariant,
} from "../../card-landing/experience-previews/CardExpInteraction";
import { TaasExpImage, TaasStaiExpImageClosingNote } from "../taas-exp/TaasExpImage";
import { TaasExpText } from "../taas-exp/TaasStaiText";
import { TaasSingleCardAudioDisplay } from "./TaasSingleCardAudioDisplay";
import { TaasStaiExpVideo } from "../taas-exp/TaasStaiExpVideo";

type Props = {
  page: {
    type: string;
    content: any;
    created_at?: string;
    cover_image_url?: string;
  };
  isFromBookView?: boolean;
  talent?: {
    id?: string | number;
    name?: string;
    avatar?: string;
  };
  template?: string;
  closingNote?: TaasStaiExpImageClosingNote;
  /** When false, Single/K-Indie book-view audio is limited to a short preview. */
  isPurchased?: boolean;
  pageId?: number;
  canInteract?: boolean;
  interactionVariant?: CardExpInteractionVariant;
  interactionTalentId?: number;
  interactionTalentName?: string;
  interactionTalentAvatar?: string;
  isSingleCardSealed?: boolean;
  /** True only for the book page the user is looking at right now. */
  isPageActive?: boolean;
};

const typeToExperienceKey = (type: string, content: any): string | null => {
  const normalizedType = type.replace(/_/g, "").toLowerCase();

  if (normalizedType === "image") {
    const urlArray = Array.isArray(content?.url) ? content.url : [];
    return urlArray.length > 1 ? "image_grid" : "image";
  }

  if (normalizedType === "imagegrid") {
    return "image_grid";
  }

  const typeMap: Record<string, string> = {
    audio: "audio",
    video: "video",
    text: "text",
  };

  return typeMap[normalizedType] || null;
};

const mapPageToExperienceData = (
  page: {
    type: string;
    content: any;
    talent?: any;
    created_at?: string;
    cover_image_url?: string;
  },
  talent?: { id?: string | number; name?: string; avatar?: string }
) => {
  const content = page.content || {};

  console.log(page, "page");
  const rawTalent = talent ?? content?.talent ?? page?.talent;
  const resolvedTalent = rawTalent
    ? {
        ...rawTalent,
        // normalise: API sends profile_image, components expect avatar
        avatar: rawTalent.profile_image || "",
      }
    : undefined;
  const key = typeToExperienceKey(page.type, content);
  if (!key) return null;

  const pickFirstString = (...vals: any[]) => {
    for (const v of vals) {
      if (typeof v === "string" && v.trim() !== "") return v;
    }
    return "";
  };

  switch (key) {
    case "image_grid":
      return {
        key,
        data: {
          imageGrid: {
            images: Array.isArray(content.url) ? content.url : [],
            description: content.description || "",
          },
          talent: resolvedTalent,
        },
      };
    case "image":
      return {
        key,
        data: {
          image: {
            imageUrl: Array.isArray(content.url) ? content.url[0] : content.url,
            title: content.title || "",
            description: content.description || "",
          },
          talent: resolvedTalent,
        },
      };
    case "text":
      return {
        key,
        data: {
          text: {
            title: content.title || "",
            description: content.description || "",
            created_at: content.created_at || page.created_at || "",
          },
          talent: resolvedTalent,
        },
      };
    case "audio":
      return {
        key,
        data: {
          audio: {
            audioUrl: content.url || "",
            description: content.description || "",
            title: content.title || "",
            artistName: content.artist_name || "",
            songTitle: content.song_title || "",
            lyricsUrl: content.lyricsUrl ?? content.lyrics_url ?? "",
            lyricsText: content.lyricsText ?? content.lyrics_text ?? content.lyrics ?? "",
            cover_image_url: page?.cover_image_url || "",
            image_url: content?.image_url || "",
            preview_url: content?.preview_url || "",
          },
          talent: resolvedTalent,
        },
      };
    case "video":
      return {
        key,
        data: {
          video: {
            videoUrl: content.url || "",
            title: content.title || "",
            thumbnail: content.thumbnail || content.poster || content.cover || "",
            description: pickFirstString(
              content.description,
              content.caption,
              content.text,
              content.message
            ),
          },
          talent: resolvedTalent,
        },
      };
    default:
      return null;
  }
};

export const CardPageContent: React.FC<Props> = ({
  page,
  isFromBookView,
  talent,
  template,
  closingNote,
  isPurchased = true,
  pageId,
  canInteract = false,
  interactionVariant,
  interactionTalentId,
  interactionTalentName,
  interactionTalentAvatar,
  isSingleCardSealed = false,
  isPageActive = true,
}) => {
  const { darkMode } = useThemeStore();
  const { isBookIconSelected } = useBookIconStore();

  if (!page || !page.type || !page.content) return null;

  const mapped = mapPageToExperienceData(page, talent);
  if (!mapped) return null;

  const { key, data: pageData } = mapped;

  // Fan thank-you footer should appear for Single Card and K-Indie ending slides.
  const normalizeTemplate = (val?: string) => (val || "").trim().toLowerCase();
  const isSingleCardTemplate = normalizeTemplate(template) === "single card template";
  const isKIndieCardTemplate = normalizeTemplate(template) === "k-indie card template";
  const shouldShowFanThankYou = isSingleCardTemplate || isKIndieCardTemplate;
  const applicableClosingNote = shouldShowFanThankYou && closingNote ? closingNote : undefined;

  const showInteraction =
    canInteract &&
    Boolean(interactionVariant) &&
    pageId != null &&
    Number(pageId) > 0 &&
    isPageActive;
  const isBookViewImage = isFromBookView && (key === "image" || key === "image_grid");

  return (
    <Box
      sx={{
        borderRadius: 2,
        // border: `1px solid ${darkMode ? "var(--neutral-700)" : "var(--neutral-200)"}`,
        backgroundColor: darkMode ? "var(--neutral-900)" : "var(--neutral-50)",
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          // overflow: isBookViewImage ? "hidden" : "auto",
          pb: isBookViewImage ? 0 : { xs: 3.5, sm: 0 },
        }}
      >
        {key === "image" && isFromBookView && (
          <TaasExpImage
            data={pageData}
            closingNote={applicableClosingNote}
            isSingleCard={shouldShowFanThankYou}
          />
        )}
        {key === "image_grid" && isFromBookView && (
          <TaasExpImage
            data={pageData}
            closingNote={applicableClosingNote}
            isSingleCard={shouldShowFanThankYou}
          />
        )}
        {key === "image" && !isFromBookView && <SingleImageDisplay data={pageData} />}
        {key === "image_grid" && !isFromBookView && <ImageGridDisplay data={pageData} />}
        {key === "text" && isFromBookView && <TaasExpText data={pageData} />}
        {key === "text" && !isFromBookView && <TextDisplay data={pageData} />}
        {
          key === "audio" && isFromBookView && (
            // (template === "Single Card Template" || template === "K-Indie Card Template" ? (
            <TaasSingleCardAudioDisplay
              key={`single-card-audio-${String(pageData.audio?.audioUrl ?? "")}-${String(pageData.audio?.lyricsUrl ?? page.content?.lyrics_url ?? "")}`}
              data={pageData}
              isPurchased={isPurchased}
            />
          )
          // ) : (
          //   <TaasExpAudio data={pageData} />
          // ))}
        }
        {key === "audio" && !isFromBookView && <AudioDisplay data={pageData} />}
        {/* Unmount when page is inactive so playback can hand off to the floating mini player.
            Book pages stay mounted within ±1 preload — without this gate, swipe never triggers promote. */}
        {key === "video" && isFromBookView && isPageActive && <TaasStaiExpVideo data={pageData} />}
        {key === "video" && !isFromBookView && <VideoDisplay data={pageData} />}
      </Box>
      {showInteraction && interactionVariant && (
        <CardExpInteraction
          pageId={Number(pageId)}
          darkMode={darkMode}
          talentId={interactionTalentId}
          talentName={interactionTalentName}
          talentAvatar={interactionTalentAvatar}
          isSingleCardSealed={isSingleCardSealed}
        />
      )}
    </Box>
  );
};
